import { DatePipe, isPlatformBrowser, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, EventEmitter, Inject, OnDestroy, OnInit, Output, PLATFORM_ID, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertFacade } from '@curbnturf/alert/src/lib/+state/alert.facade';
import { BoondockFacade, BoondockService } from '@curbnturf/boondock-listing';
import { AuthHelpers, Boondock, PATHS, User } from '@curbnturf/entities';
import { CheckedComponent } from '@curbnturf/form-helpers/src/lib/components/checked/checked.component';
import { boondockComplete } from '@curbnturf/helpers';
import { Logger } from '@curbnturf/network';
import { EnableConfirmationModalComponent } from '@curbnturf/site-components/src/lib/enable-confirmation-modal/enable-confirmation-modal.component';
import { SpinnerComponent } from '@curbnturf/ui/src/lib/spinner/spinner.component';
import { UserFacade } from '@curbnturf/user/src/lib/+state/user.facade';
import { IonicModule } from '@ionic/angular';
import { DateTime } from 'luxon';
import { of, Subscription } from 'rxjs';
import { catchError, skipWhile } from 'rxjs/operators';
import { BoondockDescriptionComponent } from '../boondock-description/boondock-description.component';
import { DirectionsComponent } from '../directions/directions.component';
import { OverviewComponent } from '../overview/overview.component';
import { PayoutComponent } from '../payout/payout.component';
import { PoliciesComponent } from '../policies/policies.component';

@Component({
  selector: 'curbnturf-boondock-edit',
  templateUrl: './boondock-edit.component.html',
  styleUrls: ['./boondock-edit.component.scss'],
  standalone: true,
  imports: [
    CheckedComponent,
    DatePipe,
    BoondockDescriptionComponent,
    DirectionsComponent,
    EnableConfirmationModalComponent,
    FormsModule,
    IonicModule,
    NgClass,
    NgFor,
    NgIf,
    OverviewComponent,
    PoliciesComponent,
    ReactiveFormsModule,
    SpinnerComponent,
  ],
})
export class BoondockEditComponent implements OnDestroy, OnInit {
  @ViewChild(EnableConfirmationModalComponent, { static: true })
  boondockEnableModal: EnableConfirmationModalComponent;

  @ViewChild(PayoutComponent) payoutComponent: PayoutComponent;

  @Output() public onBoondockLoaded: EventEmitter<Boondock> = new EventEmitter<Boondock>();

  errors: string[] = [];
  boondock: Boondock;
  user: User;

  isNew: boolean;

  section = 'description';

  spinnerOn = false;
  lastSaved: DateTime;

  showMenu: boolean = false;

  get lastSavedTime(): string {
    return this.lastSaved.toLocaleString();
  }

  private subscriptions: Subscription[] = [];

  constructor(
    private alertFacade: AlertFacade,
    private route: ActivatedRoute,
    private router: Router,
    private boondockService: BoondockService,
    private boondockFacade: BoondockFacade,
    private userFacade: UserFacade,
    private logger: Logger,
    @Inject(PLATFORM_ID) private platformId: string,
  ) {}

  ngOnInit() {
    this.spinnerOn = true;
    if (this.route && this.route.snapshot) {
      if (this.route.snapshot.url && this.route.snapshot.url.length > 0 && this.route.snapshot.url[0].path === 'new') {
        this.isNew = true;
      }
    }

    this.subscriptions.push(
      this.userFacade.currentUserOnce$.subscribe((user) => {
        this.user = user;

        this.subscriptions.push(
          this.route.paramMap.subscribe((params) => {
            const id = parseInt(params.get('id') ?? '', 10);
            if (id) {
              this.subscriptions.push(
                this.boondockService.fetch(id).subscribe({
                  next: (boondock) => {
                    if (boondock) {
                      this.boondock = new Boondock({ ...boondock });
                    } else {
                      this.alertFacade.display({
                        title: 'Error Opening Boondock',
                        body: 'An error occurred and we were unable to open the boondock for editing.',
                        level: 'error',
                      });
                    }
                  },
                  error: (error) => {
                    this.logger.error('Error Opening Boondock For Editing', error);
                    this.alertFacade.display({
                      title: 'Error Opening Boondock',
                      body: 'An error occurred and we were unable to open the boondock for editing.',
                      level: 'error',
                    });
                  },
                  complete: () => {
                    this.spinnerOn = false;
                  },
                }),
              );
            } else {
              this.alertFacade.display({
                title: 'Boondock ID Not Supplied',
                body: 'Unable to open boondock without an identifier.',
                level: 'error',
              });
              this.spinnerOn = false;
            }

            if (params.get('section')) {
              this.section = params.get('section') || 'message';
            }
          }),

          this.boondockFacade.error$.subscribe((error: string) => {
            if (error) {
              this.spinnerOn = false;
              this.errors.push(error);
            }
          }),

          this.boondockFacade.selected$
            .pipe(
              skipWhile((boondock) => !boondock),
              catchError(() => of()),
            )
            .subscribe(async (boondock: Boondock) => {
              this.spinnerOn = false;

              if (!boondock) {
                return;
              }

              if (
                !boondock.user ||
                (!(boondock.user?.id === this.user?.id) &&
                  !AuthHelpers.hasSalesOwnership(this.user, boondock?.user) &&
                  !AuthHelpers.isAdminRole(this.user))
              ) {
                await this.router.navigateByUrl(PATHS.boondocking);
              }

              if (boondock.id && this.isNew) {
                await this.router.navigate(['edit', boondock.id], {
                  relativeTo: this.route.parent,
                });
              }
            }),
        );
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  async selectNavigation(navigationString: string) {
    // Close the menu
    this.showMenu = false;

    if (this.section === navigationString) {
      return;
    }

    // scroll to top on navigation
    if (isPlatformBrowser(this.platformId) && window) {
      window.scrollTo(0, 0);
    }

    this.section = navigationString;

    if (this.boondock?.id) {
      let options: { section: string; site?: number } = { section: navigationString };

      await this.router.navigate(['boondocking', 'edit', this.boondock.id, options], {
        relativeTo: this.route.parent,
      });
    }
  }

  saved(saved: boolean) {
    if (saved) {
      this.lastSaved = DateTime.now();
    }
  }

  onShowMenu() {
    this.showMenu = true;
  }

  onHideMenu() {
    this.showMenu = false;
  }

  onToggleMenu() {
    if (this.showMenu) {
      this.onHideMenu();
    } else {
      this.onShowMenu();
    }
  }

  overviewCompleted(): boolean {
    return boondockComplete.overviewCompleted(this.boondock);
  }

  descriptionCompleted(): boolean {
    return boondockComplete.descriptionCompleted(this.boondock);
  }

  directionsCompleted(): boolean {
    return boondockComplete.directionsCompleted(this.boondock);
  }

  bookingCompleted(): boolean {
    return boondockComplete.bookingCompleted(this.boondock);
  }
}
